import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Snackbar, Alert, Menu, MenuItem, ListItemIcon, ListItemText, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon,
  Delete as DeleteIcon, Close as CloseIcon
} from '@mui/icons-material';
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useFormBuilder } from '../../hooks/useFormBuilder';
import { useFormBuilderLogic } from './hooks/useFormBuilderLogic';
import SideMenu from '../../components/form-builder/SideMenu';
import FormArea from '../../components/form-builder/FormArea';
import Settings from '../Settings';
import FormHeader from './components/FormHeader';
import FormInfo from './components/FormInfo';
import PageManager from './components/PageManager';
import FolderDialog from './components/FolderDialog';
import PreviewModal from './components/PreviewModal';
import SaveConfirmationModal from './components/SaveConfirmationModal';
// import AIFormGeneratorDialog from '../../components/AIFormGeneratorDialog'; // AI disabled
import DocumentImportArea from '../../components/DocumentImportArea';

const FormBuilder = () => {
  const { t } = useTranslation();
  const { id: urlFormId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useSelector((state) => state.loginData.loginData);

  // Form state
  const [formId, setFormId] = useState(urlFormId ? parseInt(urlFormId, 10) : Math.floor(Math.random() * 1000000));
  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // const [aiDialogOpen, setAiDialogOpen] = useState(false); // AI disabled
  const [documentImportOpen, setDocumentImportOpen] = useState(false);
  const [isImportLoading, setIsImportLoading] = useState(false);

  // Form builder hook
  const {
    formFields,
    formSettings,
    formTitle,
    formDescription,
    handleAddField,
    handleRemoveField,
    handleUpdateField,
    handleUpdateSettings,
    handleReorderFields,
    handleUpdateFormDetails,
    handleDuplicateField,
    isDirty,
    setIsDirty
  } = useFormBuilder(formId);

  // Form builder logic hook
  const {
    folders,
    folderOptions,
    selectedFolderIds,
    setSelectedFolderIds,
    folderDialogOpen,
    snackbar,
    shareDialog,
    setShareDialog,
    formLink,
    setFormLink,
    backgroundPdf,
    showPdfBackground,
    previewModalOpen,
    previewDevice,
    calculationValues,
    anchorEl,
    selectedPageId,
    saveConfirmationOpen,
    isSaving,
    handleSaveForm,
    confirmSaveForm,
    handleSaveConfirmationClose,
    fetchFolders,
    showSnackbar,
    closeSnackbar,
    handleFolderDialogOpen,
    handleFolderDialogClose,
    handleCopyLink,
    handlePreviewOpen,
    handlePreviewClose,
    handleDeviceChange,
    handleMenuOpen,
    handleMenuClose,
    handleCalculationChange,
    handlePdfBackgroundLoad
  } = useFormBuilderLogic(isDirty, setIsDirty);

  // Calculate current page info
  const maxPage = Math.max(...(formFields || []).map(f => f.page || 1), 1);
  const currentFields = (formFields || []).filter(f => (f.page || 1) === currentPage);

  // Excel import verilerini kontrol et ve işle
  useEffect(() => {
    const importedFields = location.state?.importedFields;
    const importSource = location.state?.importSource;

    if (importedFields && importSource === 'excel' && Array.isArray(importedFields)) {
      console.log('📊 Excel verilerinden alanlar import ediliyor:', importedFields);

      // Mevcut alanları temizle (eğer boş formsa)
      if (formFields.length === 0) {
        // Import edilen alanları tek tek ekle
        importedFields.forEach((field, index) => {
          console.log(`📝 Alan ${index + 1} ekleniyor:`, field);

          // handleAddField'ı field verisiyle çağır
          setTimeout(() => {
            handleAddField(field.type, field, 1);
          }, index * 100); // Kısa delay ile sıralı ekleme
        });

        // Başlık ve açıklama güncelle
        setTimeout(() => {
          const excelFormTitle = `Excel İçe Aktarımı - ${new Date().toLocaleDateString('tr-TR')}`;
          const excelFormDescription = `Bu form Excel dosyasından otomatik olarak oluşturulmuştur. Toplam ${importedFields.length} alan içermektedir.`;

          handleUpdateFormDetails(excelFormTitle, excelFormDescription);

          // Başarı mesajı göster
          showSnackbar(`Excel dosyasından ${importedFields.length} alan başarıyla import edildi!`, 'success');
        }, importedFields.length * 100 + 500);
      }

      // State'i temizle (tekrar import edilmesini önle)
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, handleAddField, handleUpdateFormDetails, navigate, formFields.length, showSnackbar]);

  // Event handlers
  const handleDrop = (e) => {
    e.preventDefault();
    const fieldType = e.dataTransfer.getData('fieldType');
    if (fieldType) {
      console.log('🎯 handleDrop çağrıldı:', fieldType, 'currentPage:', currentPage);
      handleAddField(fieldType, null, currentPage);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#2196f3';
    e.currentTarget.style.backgroundColor = '#f0f7ff';
  };

  const handleDragLeave = (e) => {
    e.currentTarget.style.borderColor = '#1976d2';
    e.currentTarget.style.backgroundColor = '#fff';
  };

  const handleDeletePage = (pageToDelete) => {
    console.log('🗑️ Sayfa siliniyor:', pageToDelete);

    // Sayfadaki tüm alanları kaldır
    const fieldsToRemove = formFields.filter(field => (field.page || 1) === pageToDelete);
    console.log('🗑️ Silinecek alanlar:', fieldsToRemove);

    fieldsToRemove.forEach(field => {
      console.log('🗑️ Alan siliniyor:', field.id);
      handleRemoveField(field.id);
    });

    // Diğer sayfaların numaralarını güncelle - daha sonraki sayfaları bir sayfa geriye kaydır
    setTimeout(() => {
      // Bu işlem FormBuilder hook'unda halledilmeli
      // Mevcut sayfa numarasını güncelle
      if (currentPage >= pageToDelete && currentPage > 1) {
        setCurrentPage(Math.max(1, currentPage - 1));
      } else if (currentPage === pageToDelete && pageToDelete > 1) {
        setCurrentPage(pageToDelete - 1);
      }
    }, 100);
  };

  const handleFieldPositionUpdate = (fieldId, position) => {
    handleUpdateField(fieldId, {
      ...formFields.find(f => f.id === fieldId),
      pdfPosition: position
    });
  };

  const handleSave = () => {
    const formData = {
      formId,
      formTitle,
      formDescription,
      formFields,
      formSettings
    };
    handleSaveForm(formData);
  };

  const handleConfirmSave = () => {
    const formData = {
      formId,
      formTitle,
      formDescription,
      formFields,
      formSettings
    };
    confirmSaveForm(formData);
  };

  // AI form generation handler - Disabled
  // AI form generation handler - Disabled
  const handleAIFormGenerated = (aiFormData) => {
    console.log('AI form generation is temporarily disabled');
    return;
  };

  // Document import handlers
  const handleDocumentImportOpen = () => {
    setDocumentImportOpen(true);
  };

  const handleDocumentImportClose = () => {
    setDocumentImportOpen(false);
    setIsImportLoading(false);
  };

  const handleDocumentImport = (parsedData) => {
    console.log('📄 Document Import:', parsedData);

    try {
      // Form başlığını güncelle
      if (parsedData.title) {
        handleUpdateFormDetails(parsedData.title, `Bu form ${new Date().toLocaleDateString('tr-TR')} tarihinde belge içe aktarımından oluşturulmuştur. Toplam ${parsedData.fields.length} alan içermektedir.`);
      }

      // Mevcut alanları temizle (eğer form boşsa) ve yeni alanları ekle
      if (formFields.length === 0) {
        // Alanları sırayla ekle
        parsedData.fields.forEach((field, index) => {
          setTimeout(() => {
            console.log(`📝 Alan ${index + 1} ekleniyor:`, field);
            handleAddField(field.type, field, 1);
          }, index * 100);
        });

        // Başarı mesajı göster
        setTimeout(() => {
          showSnackbar(`📄 Belgeden ${parsedData.fields.length} alan başarıyla aktarıldı!`, 'success');
          handleDocumentImportClose();
        }, parsedData.fields.length * 100 + 500);
      } else {
        // Form dolu ise kullanıcıya sor
        if (window.confirm('Mevcut form alanları silinip belge içeriği yüklensin mi?')) {
          // Önce tüm alanları sil
          formFields.forEach(field => {
            handleRemoveField(field.id);
          });

          // Sonra yeni alanları ekle
          setTimeout(() => {
            if (parsedData.title) {
              handleUpdateFormDetails(parsedData.title, `Bu form ${new Date().toLocaleDateString('tr-TR')} tarihinde belge içe aktarımından oluşturulmuştur. Toplam ${parsedData.fields.length} alan içermektedir.`);
            }

            parsedData.fields.forEach((field, index) => {
              setTimeout(() => {
                console.log(`📝 Alan ${index + 1} ekleniyor:`, field);
                handleAddField(field.type, field, 1);
              }, index * 100);
            });

            setTimeout(() => {
              showSnackbar(`📄 Form belgeden yeniden oluşturuldu!`, 'success');
              handleDocumentImportClose();
            }, parsedData.fields.length * 100 + 500);
          }, 500);
        } else {
          handleDocumentImportClose();
        }
      }

    } catch (error) {
      console.error('Document import error:', error);
      showSnackbar('Belge içe aktarımında hata oluştu: ' + error.message, 'error');
      handleDocumentImportClose();
    }
  };

  // EmptyFormMessage bileşeni
  const EmptyFormMessage = () => (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        width: '100%',
        textAlign: 'center'
      }}
    >
      <Typography
        variant="h6"
        color="text.secondary"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}
      >
        <Box component="span" sx={{ fontSize: '24px', color: 'primary.main' }}>
          ↓
        </Box>
        {t('form_builder')}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {t('drag_or_click_to_add_field')}
        <Box component="span" sx={{ mx: 1, color: 'primary.main', fontWeight: 'bold' }}>
          {t('drag_or_click_to_add_field')}
        </Box>
      </Typography>
    </Box>
  );

  return (
    <Box sx={{
      height: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      pt: 3,
      bgcolor: '#f8fafc'
    }}>
      <FormHeader
        formTitle={formTitle}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onPreviewOpen={handlePreviewOpen}
        onSaveForm={handleSave}
        onAIFormOpen={() => { }} // AI disabled
        onDocumentImport={handleDocumentImportOpen}
        isDirty={isDirty}
        isSaving={isSaving}
      />      <Box sx={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        px: 3,
        pb: 3,
        gap: 2
      }}>
        {activeTab === 0 && (
          <>
            <SideMenu
              onAddField={(fieldType) => {
                console.log('🎯 SideMenu onAddField çağrıldı:', fieldType, 'currentPage:', currentPage);
                handleAddField(fieldType, null, currentPage);
              }}
              currentPage={currentPage}
            />
            <Box
              sx={{
                flex: 1,
                p: 2,
                bgcolor: 'background.default',
                overflowY: 'auto',
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}
            >
              <FormInfo
                formTitle={formTitle}
                formDescription={formDescription}
                onUpdateFormDetails={handleUpdateFormDetails}
                userData={userData}
                selectedFolderIds={selectedFolderIds}
                setSelectedFolderIds={setSelectedFolderIds}
                folderOptions={folderOptions}
                onFolderDialogOpen={handleFolderDialogOpen}
              />

              <Box
                sx={{
                  minHeight: 400,
                  border: '2px dashed',
                  borderColor: '#e2e8f0',
                  borderRadius: '12px',
                  p: 2,
                  backgroundColor: '#ffffff',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#1976d2',
                    bgcolor: '#f8fafc',
                    boxShadow: '0 4px 6px rgba(25, 118, 210, 0.1)'
                  },
                  position: 'relative'
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <FormArea
                  formFields={currentFields}
                  onRemoveField={handleRemoveField}
                  onUpdateField={handleUpdateField}
                  onReorderFields={handleReorderFields}
                  onDuplicateField={handleDuplicateField}
                  calculationValues={calculationValues}
                  onCalculationChange={handleCalculationChange}
                  backgroundPdf={backgroundPdf}
                  showPdfBackground={showPdfBackground}
                  onFieldPositionUpdate={handleFieldPositionUpdate}
                  currentPage={currentPage}
                />
                {currentFields.length === 0 && <EmptyFormMessage />}
              </Box>

              <PageManager
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                maxPage={maxPage}
                currentFields={currentFields}
                onDeletePage={handleDeletePage}
              />
            </Box>
          </>
        )}

        {activeTab === 1 && (
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: '100%'
          }}>
            {console.log('⚙️ Settings render, formSettings:', formSettings)}
            <Settings
              settings={formSettings}
              onUpdateSettings={handleUpdateSettings}
            />
          </Box>
        )}
      </Box>

      {/* Klasör Seçim Diyaloğu */}
      <FolderDialog
        open={folderDialogOpen}
        onClose={handleFolderDialogClose}
        formTitle={formTitle}
        folderOptions={folderOptions}
        selectedFolderIds={selectedFolderIds}
        setSelectedFolderIds={setSelectedFolderIds}
      />

      {/* Önizleme Modal */}
      <PreviewModal
        open={previewModalOpen}
        onClose={handlePreviewClose}
        previewDevice={previewDevice}
        onDeviceChange={handleDeviceChange}
        formTitle={formTitle}
        formDescription={formDescription}
        formFields={formFields}
        formSettings={formSettings}
        logoUrl={userData?.logoUrl}
        calculationValues={calculationValues}
      />

      {/* Save Confirmation Modal */}
      <SaveConfirmationModal
        open={saveConfirmationOpen}
        onClose={handleSaveConfirmationClose}
        onConfirm={handleConfirmSave}
        loading={isSaving}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleCalculationChange(selectedPageId, 'up')}>
          <ListItemIcon>
            <ArrowUpwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('move_up')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleCalculationChange(selectedPageId, 'down')}>
          <ListItemIcon>
            <ArrowDownwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('move_down')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDuplicateField(selectedPageId)}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('copy_page')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => handleRemoveField(selectedPageId)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('delete_page')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Document Import Dialog */}
      <Dialog
        open={documentImportOpen}
        onClose={handleDocumentImportClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 24,
          sx: {
            maxHeight: '80vh',
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e0e0e0',
          background: 'linear-gradient(to right, #f7f9fc, #edf2f7)',
          py: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span">
            📄 Belge İçe Aktarma
          </Typography>
          <IconButton
            onClick={handleDocumentImportClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <DocumentImportArea
            onImport={handleDocumentImport}
            isLoading={isImportLoading}
            onLoadingChange={setIsImportLoading}
          />
        </DialogContent>
        <DialogActions sx={{
          borderTop: '1px solid #e0e0e0',
          p: 2.5,
          bgcolor: '#f9fafc'
        }}>
          <Button
            onClick={handleDocumentImportClose}
            variant="outlined"
            sx={{ px: 2 }}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormBuilder;
